// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ProjectModel } from '../model/project.model';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';

@Injectable({providedIn: 'root'})
export class ProjectMetaElementService {

    constructor(
        private projectModel: ProjectModel,
        private articleManager: ArticleManager,
        private containerService: ContainerService
    ) {}

    public getMetaElementByUsage(articleUsageId: string, metaElementName: string): MetaElementVO {
      const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(articleUsageId);
      if (articleUsage) {
        return this.getMetaElement(articleUsage.articleId, articleUsage.articleVersion, metaElementName);
      } else {
        return null;
      }
    }

    public getMetaElementUsage(articleUsageId: string, metaElementName: string): MetaElementUsageVO {
      const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(articleUsageId);
      return ArticleUsageUtils.findMetaElementUsageByName(articleUsage, metaElementName);
    }

    public getConnectionAreaUsage(articleUsageId: string, metaElementName: string): ConnectionAreaUsageVO {
      const articleUsage = this.projectModel.getArticleUsage(articleUsageId);
      return ArticleUsageUtils.findConnectionAreaUsageByName(articleUsage, metaElementName);
    }

    public getMetaElement(articleId: number, version: number, metaElementName: string): MetaElementVO {
      const article: ArticleVO = this.articleManager.getCachedArticle(articleId, version);
      if (article != null) {
        for (const meta of article.metaElements) {
          if (meta.name === metaElementName) {
            return meta;
          }
        }
      }
      return null;
    }

    public addMetaElementUsage(articleUsageGuid: string, metaElement: MetaElementVO): MetaElementUsageVO {
      const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(articleUsageGuid);
      return this.addMetaElementUsageToArticleUsage(metaElement, articleUsage);
    }

    public createUsageBetweenConnections(givingCube: Connection, acceptingCube: Connection): void {
      const givingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(givingCube);
      const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);

      if (!acceptingCube.metaElementUsage) {
        acceptingCube.metaElementUsage = this.addMetaElementUsage(acceptingArticleContainer.articleGUID, acceptingCube.metaElementVO);
      }
      const acceptingUsage: ConnectionAreaUsageVO = acceptingCube.metaElementUsage as ConnectionAreaUsageVO;
      if (!givingCube.metaElementUsage) {
        givingCube.metaElementUsage = this.addMetaElementUsage(givingArticleContainer.articleGUID, givingCube.metaElementVO);
      }
      const givingUsage: ConnectionAreaUsageVO = givingCube.metaElementUsage as ConnectionAreaUsageVO;

      ConnectionAreaUsageUtils.connect(acceptingUsage, givingUsage);
    }

    public addMetaElementUsageToArticleUsage(metaElement: MetaElementVO, articleUsage: ArticleUsageVO): MetaElementUsageVO {
      const metaElementUsage: MetaElementUsageVO = this.getMetaElementUsage(articleUsage.id, metaElement.name);
      if (metaElementUsage) {
        // return existing
        return metaElementUsage;
      }
      if (metaElement instanceof ConnectionAreaVO) {
        return this.addConnectionAreaUsageToArticle(articleUsage, metaElement);
      }
      if (metaElement instanceof TextureAreaVO) {
        const textureAreaUsage = ArticleUsageUtils.newTextureAreaUsage(articleUsage, metaElement.name);
        if (ArticleUtils.isTextTextureType(metaElement.textureType)) {
          textureAreaUsage.textValue = ConnectionInfoUtils.getTextValueByTextureArea(metaElement);
        }
        return textureAreaUsage;
      }
      return null;
    }

    public addConnectionAreaUsageToArticle(articleUsage: ArticleUsageVO, connectionArea: ConnectionAreaVO) {
      const connectionAreaUsage = this.getConnectionAreaUsage(articleUsage.id, connectionArea.name);
      if (connectionAreaUsage) {
        // return existing
        return connectionAreaUsage;
      }

      return ArticleUsageUtils.newConnectionAreaUsage(articleUsage, connectionArea.name);
    }
}
